import React from 'react';
import { Select, Tooltip } from 'antd';
import { debounce } from 'lodash';

import { PaginatedResponse, SelectableItems } from '../../lib/PlannerMetadata';

const { Option } = Select;

interface TooltipOptions {
  tooltipPlacement?:
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
    | 'leftTop'
    | 'leftBottom'
    | 'rightTop'
    | 'rightBottom';
  tooltipEnabled?: boolean;
  tooltipTitle?: string;
}

interface APIParamOptions {
  fetchLimit?: number;
  facet?: string;
  offset?: number;
  sortBy?: string;
  type?: string;
}

interface AutoSuggestInputProps extends TooltipOptions, APIParamOptions {
  availableValues?: { [key: string]: string };
  initialValue?: string | string[];
  disabled?: boolean;
  placeholder: string;
  multiple: boolean;
  style: { [key: string]: number | string };
  addToChoiceInput?: (value: string) => void;
  api: (
    name: string,
    limit?: number,
    offset?: number,
    sortBy?: string,
    facet?: string,
    type?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any;
  allowClear: boolean;
  bordered?: boolean;
}

interface AutoSuggestInputStates {
  data: Set<string>;
  value: string | string[];
}

export default class AutoSuggestInput extends React.Component<
  AutoSuggestInputProps,
  AutoSuggestInputStates
> {
  static defaultProps = {
    tooltipContent: 'Start typing to get relevant suggestions.',
    tooltipPlacement: 'right',
    fetchLimit: 10,
    initialValue: '',
    disabled: false,
    multiple: false,
    bordered: true,
  };

  private loadingSet = new Set(['Loading...']);

  constructor(props) {
    super(props);

    this.state = {
      data: new Set([]),
      value: this.props.multiple ? [] : '',
    };

    const { fetchLimit, offset, sortBy, facet, type } = this.props;

    // this pre-loads some initial suggestion to show on focus
    this.debouncedSearch('', fetchLimit, offset, sortBy, facet, type);
  }
  handleSearch = async (value) => {
    if (value === '') return;
    this.setState({ data: this.loadingSet });
    // this adds a brief delay to avoid searching before user finished typing
    const { fetchLimit, offset, sortBy, facet, type } = this.props;
    await this.debouncedSearch(value, fetchLimit, offset, sortBy, facet, type);
  };

  debouncedSearch = debounce(
    async (value, limit, offset, sortBy, facet, type) => {
      await this.props
        .api(value, limit, offset, sortBy, facet, type)
        .then((results: PaginatedResponse<SelectableItems[]>) => {
          // Avoid suggesting items with the same name multiple times
          const itemNames: Set<string> = new Set();
          results.items.map((d) => {
            d.getValue() && d.getValue() != ''
              ? itemNames.add(d.getValue())
              : {};
          });
          this.setState({
            ...this.state,
            data: itemNames,
          });
        });
    },
    450,
  );

  handleChange = (value) => {
    this.setState({ value: value });
    if (this.props.multiple && Array.isArray(value)) {
      this.props.addToChoiceInput(value.join(',')); // convert array to string
    } else {
      this.props.addToChoiceInput(value);
    }
  };

  render() {
    const {
      tooltipEnabled,
      tooltipPlacement,
      tooltipTitle,
      placeholder,
      style,
      initialValue,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      availableValues,
      disabled,
      multiple,
      allowClear,
      bordered,
    } = this.props;

    // Fixed for Custom Segment Duplicate
    const options = [];

    this.state.data.forEach((d) => {
      options.push(<Option key={d}>{d}</Option>);
    });

    let value = initialValue || this.state.value;

    if (multiple && !Array.isArray(value)) {
      if (value) value = value.split(',');
      else value = [];
    }

    const selectElement = (
      <Select
        mode={multiple ? 'multiple' : undefined}
        showSearch
        value={value}
        placeholder={placeholder}
        style={style}
        defaultActiveFirstOption={false}
        onSearch={this.handleSearch}
        onChange={this.handleChange}
        notFoundContent={null}
        disabled={disabled}
        allowClear={allowClear}
        bordered={bordered}
        popupMatchSelectWidth={false}
      >
        {options}
      </Select>
    );

    return tooltipEnabled ? (
      <Tooltip placement={tooltipPlacement} title={tooltipTitle}>
        {selectElement}
      </Tooltip>
    ) : (
      selectElement
    );
  }
}
